import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Avatar,
  Divider,
  Alert,
  LinearProgress,
  Tooltip,
  Badge,
  Menu,
  ListItemButton
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Download as DownloadIcon,
  Upload as UploadIcon,
  History as HistoryIcon,
  CheckCircle as ApproveIcon,
  Build as ConstructionIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  MoreVert as MoreIcon,
  Description as FileIcon,
  Image as ImageIcon,
  PictureAsPdf as PdfIcon,
  InsertDriveFile as GenericFileIcon,
  CloudUpload as CloudUploadIcon,
  Assignment as AssignmentIcon,
  Schedule as ScheduleIcon,
  Person as PersonIcon,
  Business as ProjectIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';

const Drawings = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [drawings, setDrawings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState('create'); // create, edit, view, revisions
  const [selectedDrawing, setSelectedDrawing] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [disciplineFilter, setDisciplineFilter] = useState('all');
  const [tabValue, setTabValue] = useState(0);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [stats, setStats] = useState({
    total: 0,
    draft: 0,
    underReview: 0,
    approved: 0,
    issued: 0
  });

  const [formData, setFormData] = useState({
    drawingNumber: '',
    title: '',
    description: '',
    discipline: '',
    project: '',
    scale: '',
    sheetSize: 'A1',
    drawingType: 'plan',
    status: 'draft',
    priority: 'medium',
    tags: [],
    files: []
  });

  const disciplines = [
    'Architectural',
    'Structural',
    'Mechanical',
    'Electrical',
    'Plumbing',
    'Civil',
    'Landscape',
    'Fire Protection'
  ];

  const drawingTypes = [
    'plan',
    'elevation',
    'section',
    'detail',
    'schedule',
    'specification',
    'diagram'
  ];

  const sheetSizes = ['A0', 'A1', 'A2', 'A3', 'A4', 'B0', 'B1', 'B2', 'B3', 'B4'];
  const priorities = ['low', 'medium', 'high', 'urgent'];
  const statuses = ['draft', 'under_review', 'approved', 'issued', 'superseded', 'void'];

  useEffect(() => {
    fetchDrawings();
    fetchStats();
  }, [searchTerm, statusFilter, disciplineFilter]);

  const fetchDrawings = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (disciplineFilter !== 'all') params.append('discipline', disciplineFilter);
      
      const response = await api.get(`/drawings?${params.toString()}`);
      setDrawings(response.data);
    } catch (error) {
      console.error('Error fetching drawings:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await api.get('/drawings/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleOpenDialog = (mode, drawing = null) => {
    setDialogMode(mode);
    setSelectedDrawing(drawing);
    if (drawing) {
      setFormData({
        drawingNumber: drawing.drawingNumber || '',
        title: drawing.title || '',
        description: drawing.description || '',
        discipline: drawing.discipline || '',
        project: drawing.project || '',
        scale: drawing.scale || '',
        sheetSize: drawing.sheetSize || 'A1',
        drawingType: drawing.drawingType || 'plan',
        status: drawing.status || 'draft',
        priority: drawing.priority || 'medium',
        tags: drawing.tags || [],
        files: drawing.files || []
      });
    } else {
      setFormData({
        drawingNumber: '',
        title: '',
        description: '',
        discipline: '',
        project: '',
        scale: '',
        sheetSize: 'A1',
        drawingType: 'plan',
        status: 'draft',
        priority: 'medium',
        tags: [],
        files: []
      });
    }
    setSelectedFiles([]);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedDrawing(null);
    setSelectedFiles([]);
    setUploadProgress(0);
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    setSelectedFiles(files);
  };

  const handleSubmit = async () => {
    try {
      const submitData = new FormData();
      
      // Add form fields
      Object.keys(formData).forEach(key => {
        if (key === 'tags' && Array.isArray(formData[key])) {
          formData[key].forEach(tag => submitData.append('tags[]', tag));
        } else if (key !== 'files') {
          submitData.append(key, formData[key]);
        }
      });
      
      // Add files
      selectedFiles.forEach(file => {
        submitData.append('files', file);
      });

      let response;
      if (dialogMode === 'create') {
        response = await api.post('/drawings', submitData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (progressEvent) => {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(progress);
          }
        });
      } else {
        response = await api.put(`/drawings/${selectedDrawing._id}`, submitData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (progressEvent) => {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(progress);
          }
        });
      }

      await fetchDrawings();
      await fetchStats();
      handleCloseDialog();
    } catch (error) {
      console.error('Error saving drawing:', error);
    }
  };

  const handleDelete = async (drawingId) => {
    if (window.confirm('Are you sure you want to delete this drawing?')) {
      try {
        await api.delete(`/drawings/${drawingId}`);
        await fetchDrawings();
        await fetchStats();
      } catch (error) {
        console.error('Error deleting drawing:', error);
      }
    }
  };

  const handleApprove = async (drawingId) => {
    try {
      await api.post(`/drawings/${drawingId}/approve`);
      await fetchDrawings();
      await fetchStats();
    } catch (error) {
      console.error('Error approving drawing:', error);
    }
  };

  const handleIssueForConstruction = async (drawingId) => {
    try {
      await api.post(`/drawings/${drawingId}/issue`);
      await fetchDrawings();
      await fetchStats();
    } catch (error) {
      console.error('Error issuing drawing:', error);
    }
  };

  const handleDownload = async (drawingId, fileId) => {
    try {
      const response = await api.get(`/drawings/${drawingId}/download/${fileId}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `drawing-${drawingId}-${fileId}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading file:', error);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      draft: 'default',
      under_review: 'warning',
      approved: 'success',
      issued: 'primary',
      superseded: 'secondary',
      void: 'error'
    };
    return colors[status] || 'default';
  };

  const getPriorityColor = (priority) => {
    const colors = {
      low: 'success',
      medium: 'warning',
      high: 'error',
      urgent: 'error'
    };
    return colors[priority] || 'default';
  };

  const getFileIcon = (filename) => {
    const extension = filename.split('.').pop().toLowerCase();
    switch (extension) {
      case 'pdf': return <PdfIcon />;
      case 'jpg': case 'jpeg': case 'png': case 'gif': return <ImageIcon />;
      case 'dwg': case 'dxf': return <FileIcon />;
      default: return <GenericFileIcon />;
    }
  };

  const filteredDrawings = drawings.filter(drawing => {
    const matchesSearch = !searchTerm || 
      drawing.drawingNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      drawing.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      drawing.description?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || drawing.status === statusFilter;
    const matchesDiscipline = disciplineFilter === 'all' || drawing.discipline === disciplineFilter;
    
    return matchesSearch && matchesStatus && matchesDiscipline;
  });

  const renderDrawingDialog = () => {
    return (
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {dialogMode === 'create' && 'Create New Drawing'}
          {dialogMode === 'edit' && 'Edit Drawing'}
          {dialogMode === 'view' && 'Drawing Details'}
          {dialogMode === 'revisions' && 'Drawing Revisions'}
        </DialogTitle>
        <DialogContent>
          {dialogMode === 'revisions' ? (
            <Box>
              <Typography variant="h6" gutterBottom>
                Revision History
              </Typography>
              <List>
                {selectedDrawing?.revisions?.map((revision, index) => (
                  <ListItem key={index}>
                    <ListItemIcon>
                      <HistoryIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary={`Revision ${revision.revisionNumber}`}
                      secondary={
                        <Box>
                          <Typography variant="body2">{revision.description}</Typography>
                          <Typography variant="caption">
                            By {revision.author?.name} on {new Date(revision.date).toLocaleDateString()}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          ) : (
            <Box>
              <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)} sx={{ mb: 3 }}>
                <Tab label="Basic Info" />
                <Tab label="Files" />
                <Tab label="Metadata" />
              </Tabs>
              
              {tabValue === 0 && (
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label="Drawing Number"
                      value={formData.drawingNumber}
                      onChange={(e) => handleInputChange('drawingNumber', e.target.value)}
                      disabled={dialogMode === 'view'}
                      required
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <FormControl fullWidth>
                      <InputLabel>Discipline</InputLabel>
                      <Select
                        value={formData.discipline}
                        onChange={(e) => handleInputChange('discipline', e.target.value)}
                        disabled={dialogMode === 'view'}
                      >
                        {disciplines.map(discipline => (
                          <MenuItem key={discipline} value={discipline}>
                            {discipline}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <TextField
                      fullWidth
                      label="Title"
                      value={formData.title}
                      onChange={(e) => handleInputChange('title', e.target.value)}
                      disabled={dialogMode === 'view'}
                      required
                    />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <TextField
                      fullWidth
                      label="Description"
                      value={formData.description}
                      onChange={(e) => handleInputChange('description', e.target.value)}
                      disabled={dialogMode === 'view'}
                      multiline
                      rows={3}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label="Project"
                      value={formData.project}
                      onChange={(e) => handleInputChange('project', e.target.value)}
                      disabled={dialogMode === 'view'}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label="Scale"
                      value={formData.scale}
                      onChange={(e) => handleInputChange('scale', e.target.value)}
                      disabled={dialogMode === 'view'}
                      placeholder="e.g., 1:100"
                    />
                  </Grid>
                </Grid>
              )}
              
              {tabValue === 1 && (
                <Box>
                  {dialogMode !== 'view' && (
                    <Box sx={{ mb: 3 }}>
                      <input
                        accept=".pdf,.dwg,.dxf,.jpg,.jpeg,.png"
                        style={{ display: 'none' }}
                        id="file-upload"
                        multiple
                        type="file"
                        onChange={handleFileSelect}
                      />
                      <label htmlFor="file-upload">
                        <Button
                          variant="outlined"
                          component="span"
                          startIcon={<CloudUploadIcon />}
                          fullWidth
                          sx={{ py: 2 }}
                        >
                          Select Files to Upload
                        </Button>
                      </label>
                      {selectedFiles.length > 0 && (
                        <Box sx={{ mt: 2 }}>
                          <Typography variant="subtitle2" gutterBottom>
                            Selected Files:
                          </Typography>
                          <List dense>
                            {selectedFiles.map((file, index) => (
                              <ListItem key={index}>
                                <ListItemIcon>
                                  {getFileIcon(file.name)}
                                </ListItemIcon>
                                <ListItemText
                                  primary={file.name}
                                  secondary={`${(file.size / 1024 / 1024).toFixed(2)} MB`}
                                />
                              </ListItem>
                            ))}
                          </List>
                        </Box>
                      )}
                      {uploadProgress > 0 && uploadProgress < 100 && (
                        <Box sx={{ mt: 2 }}>
                          <LinearProgress variant="determinate" value={uploadProgress} />
                          <Typography variant="body2" sx={{ mt: 1 }}>
                            Uploading... {uploadProgress}%
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  )}
                  
                  {formData.files.length > 0 && (
                    <Box>
                      <Typography variant="subtitle2" gutterBottom>
                        Current Files:
                      </Typography>
                      <List>
                        {formData.files.map((file, index) => (
                          <ListItem key={index}>
                            <ListItemIcon>
                              {getFileIcon(file.filename)}
                            </ListItemIcon>
                            <ListItemText
                              primary={file.filename}
                              secondary={`${file.fileType} • ${(file.fileSize / 1024 / 1024).toFixed(2)} MB`}
                            />
                            <IconButton
                              onClick={() => handleDownload(selectedDrawing?._id, file._id)}
                              size="small"
                            >
                              <DownloadIcon />
                            </IconButton>
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  )}
                </Box>
              )}
              
              {tabValue === 2 && (
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <FormControl fullWidth>
                      <InputLabel>Drawing Type</InputLabel>
                      <Select
                        value={formData.drawingType}
                        onChange={(e) => handleInputChange('drawingType', e.target.value)}
                        disabled={dialogMode === 'view'}
                      >
                        {drawingTypes.map(type => (
                          <MenuItem key={type} value={type}>
                            {type.charAt(0).toUpperCase() + type.slice(1)}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <FormControl fullWidth>
                      <InputLabel>Sheet Size</InputLabel>
                      <Select
                        value={formData.sheetSize}
                        onChange={(e) => handleInputChange('sheetSize', e.target.value)}
                        disabled={dialogMode === 'view'}
                      >
                        {sheetSizes.map(size => (
                          <MenuItem key={size} value={size}>
                            {size}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <FormControl fullWidth>
                      <InputLabel>Status</InputLabel>
                      <Select
                        value={formData.status}
                        onChange={(e) => handleInputChange('status', e.target.value)}
                        disabled={dialogMode === 'view'}
                      >
                        {statuses.map(status => (
                          <MenuItem key={status} value={status}>
                            {status.replace('_', ' ').charAt(0).toUpperCase() + status.replace('_', ' ').slice(1)}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <FormControl fullWidth>
                      <InputLabel>Priority</InputLabel>
                      <Select
                        value={formData.priority}
                        onChange={(e) => handleInputChange('priority', e.target.value)}
                        disabled={dialogMode === 'view'}
                      >
                        {priorities.map(priority => (
                          <MenuItem key={priority} value={priority}>
                            {priority.charAt(0).toUpperCase() + priority.slice(1)}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          {dialogMode !== 'view' && dialogMode !== 'revisions' && (
            <Button onClick={handleSubmit} variant="contained">
              {dialogMode === 'create' ? 'Create' : 'Update'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    );
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <LinearProgress />
        <Typography sx={{ mt: 2 }}>Loading drawings...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            Drawings
          </Typography>
          <Typography variant="body1" color="textSecondary">
            Manage construction drawings and documentation
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/drawings/create')}
        >
          Add Drawing
        </Button>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Total Drawings
                  </Typography>
                  <Typography variant="h5">
                    {stats.total}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'primary.main' }}>
                  <FileIcon />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Draft
                  </Typography>
                  <Typography variant="h5">
                    {stats.draft}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'grey.500' }}>
                  <AssignmentIcon />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Under Review
                  </Typography>
                  <Typography variant="h5">
                    {stats.underReview}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'warning.main' }}>
                  <ScheduleIcon />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Approved
                  </Typography>
                  <Typography variant="h5">
                    {stats.approved}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'success.main' }}>
                  <ApproveIcon />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Issued
                  </Typography>
                  <Typography variant="h5">
                    {stats.issued}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'info.main' }}>
                  <ConstructionIcon />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                placeholder="Search drawings..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <MenuItem value="all">All Statuses</MenuItem>
                  {statuses.map(status => (
                    <MenuItem key={status} value={status}>
                      {status.replace('_', ' ').charAt(0).toUpperCase() + status.replace('_', ' ').slice(1)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <FormControl fullWidth>
                <InputLabel>Discipline</InputLabel>
                <Select
                  value={disciplineFilter}
                  onChange={(e) => setDisciplineFilter(e.target.value)}
                >
                  <MenuItem value="all">All Disciplines</MenuItem>
                  {disciplines.map(discipline => (
                    <MenuItem key={discipline} value={discipline}>
                      {discipline}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, md: 2 }}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<FilterIcon />}
                onClick={() => {
                  setSearchTerm('');
                  setStatusFilter('all');
                  setDisciplineFilter('all');
                }}
              >
                Clear
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Drawings Table */}
      <Card>
        <CardContent>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Drawing Number</TableCell>
                  <TableCell>Title</TableCell>
                  <TableCell>Discipline</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Priority</TableCell>
                  <TableCell>Files</TableCell>
                  <TableCell>Last Modified</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredDrawings.map((drawing) => (
                  <TableRow key={drawing._id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {drawing.drawingNumber}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {drawing.title}
                      </Typography>
                      {drawing.description && (
                        <Typography variant="caption" color="textSecondary" display="block">
                          {drawing.description.substring(0, 50)}...
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={drawing.discipline} 
                        size="small" 
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={drawing.status?.replace('_', ' ')} 
                        color={getStatusColor(drawing.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={drawing.priority} 
                        color={getPriorityColor(drawing.priority)}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Badge badgeContent={drawing.files?.length || 0} color="primary">
                        <FileIcon />
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {new Date(drawing.updatedAt).toLocaleDateString()}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title="View">
                          <IconButton 
                            size="small" 
                            onClick={() => handleOpenDialog('view', drawing)}
                          >
                            <ViewIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit">
                          <IconButton 
                            size="small" 
                            onClick={() => handleOpenDialog('edit', drawing)}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Revisions">
                          <IconButton 
                            size="small" 
                            onClick={() => handleOpenDialog('revisions', drawing)}
                          >
                            <HistoryIcon />
                          </IconButton>
                        </Tooltip>
                        {drawing.status === 'under_review' && (
                          <Tooltip title="Approve">
                            <IconButton 
                              size="small" 
                              color="success"
                              onClick={() => handleApprove(drawing._id)}
                            >
                              <ApproveIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        {drawing.status === 'approved' && (
                          <Tooltip title="Issue for Construction">
                            <IconButton 
                              size="small" 
                              color="primary"
                              onClick={() => handleIssueForConstruction(drawing._id)}
                            >
                              <ConstructionIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Delete">
                          <IconButton 
                            size="small" 
                            color="error"
                            onClick={() => handleDelete(drawing._id)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          
          {filteredDrawings.length === 0 && (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="h6" color="textSecondary" gutterBottom>
                No drawings found
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {searchTerm || statusFilter !== 'all' || disciplineFilter !== 'all'
                  ? 'Try adjusting your search criteria'
                  : 'Get started by creating your first drawing'
                }
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {renderDrawingDialog()}
    </Box>
  );
};

export default Drawings;